// src/lib/anthropic-models.ts
//
// Single source of truth for Anthropic model IDs referenced anywhere
// in this codebase.
//
// Why this file exists: we shipped a production bug where the AI
// Tutor route called model `claude-haiku-4-20250414` — a model ID
// that doesn't exist on the Anthropic API. The wrong ID was also in
// the pricing table and in the pricing test, all three out of sync
// with the real Haiku 4.5 ID. The bug surfaced to users as
// "Stream error" with no obvious cause until someone traced the
// request → 404 → SSE catch block. Centralizing model IDs here makes
// that class of bug impossible: there's only ONE place to look up
// the canonical string, and a drift test (below) fails CI if any
// `'claude-...'` string literal anywhere in `src/` isn't in this
// registry.
//
// Conventions:
//   - CURRENT_ANTHROPIC_MODELS — the IDs new code should call.
//     Use the constant, not the raw string. Imports look like:
//        client.messages.create({ model: CURRENT_ANTHROPIC_MODELS.HAIKU, ... })
//   - HISTORICAL_ANTHROPIC_MODELS — IDs that only exist for
//     backwards-compat lookups (e.g. pricing for old usage-tracking
//     rows). Should NOT be passed to messages.* calls.
//   - TEST_FIXTURE_MODEL_IDS — synthetic IDs used by tests to cover
//     "unknown model" code paths.

/**
 * Active Anthropic models that the application calls. New code MUST
 * import from this constant rather than pasting raw model strings.
 *
 * Keys are short semantic names (OPUS, SONNET, HAIKU); values are
 * the canonical Anthropic model IDs. When a model bump is needed,
 * update the value in ONE place — every call site re-resolves.
 */
export const CURRENT_ANTHROPIC_MODELS = {
  /** Top quality — used for generation, audit, anything where output
   * accuracy beats throughput. Bumped 4.7 → 4.8 (latest Opus). */
  OPUS: 'claude-opus-4-8',
  /** Latest-Sonnet alias — used by the question generator AND the
   * tutor thread. The undated alias resolves to the most recent
   * Sonnet snapshot, so it never goes stale the way a pinned date
   * does (see SONNET_4_MAY2025 in HISTORICAL — it retired 2026-06-15
   * and 404'd the tutor). */
  SONNET: 'claude-sonnet-4-6',
  /** Cheap + fast — used by tutor hints and the ACT tutor chat. The
   * SAT per-question chat moved to SONNET (build-7 retest, F9: Haiku
   * invented a false equality when asked to combine unrelated items). */
  HAIKU: 'claude-haiku-4-5-20251001',
} as const

/**
 * Models referenced only by the pricing table for backwards-compat
 * cost lookups against historical UsageTracking rows. NOT to be
 * passed to messages.* calls — these may not exist on the live API
 * anymore.
 *
 * When the oldest pricing entry no longer matches any historical
 * usage row, it can be removed from both this list and the pricing
 * table.
 */
export const HISTORICAL_ANTHROPIC_MODELS = [
  /** Sonnet 4 (May 2025 snapshot). RETIRED 2026-06-15 — must NOT be
   * passed to messages.* (returns 404). Kept only so the pricing
   * table can cost historical usage_tracking rows that recorded it,
   * and so the drift test still recognizes the string. */
  'claude-sonnet-4-20250514',
  /** Pre-bump Opus. May still appear in old usage_tracking rows. */
  'claude-opus-4-5-20250929',
  /** Previous Opus (pre 4.8 bump). Kept for pricing/cost lookups on
   * historical usage rows. */
  'claude-opus-4-7',
  /** Sonnet routed via Claude Code's runtime — recorded distinctly so
   * we can attribute cost to the right surface, but it's the same
   * underlying model. */
  'claude-sonnet-4-6-via-claude-code',
  /** Opus routed via Claude Code's runtime. Used by prisma/ingest-*
   * scripts for audit/lesson ingestion. Same model under the hood
   * as the regular Opus alias; the suffix tracks the call surface
   * for cost attribution. */
  'claude-opus-4-7-via-claude-code',
] as const

/**
 * Synthetic model IDs that appear in test fixtures (e.g. to cover
 * the "unknown model returns $0" code path in usage-pricing). These
 * are NEVER real model IDs — they're test-only.
 */
export const TEST_FIXTURE_MODEL_IDS = ['claude-future-99-99999999', 'claude-future-99'] as const

export type CurrentAnthropicModelId =
  (typeof CURRENT_ANTHROPIC_MODELS)[keyof typeof CURRENT_ANTHROPIC_MODELS]

/**
 * Every Anthropic model string the codebase is allowed to contain.
 * The drift test enumerates `'claude-...'` literals across `src/`
 * and asserts every match is in this set.
 */
export const KNOWN_ANTHROPIC_MODEL_IDS: ReadonlySet<string> = new Set<string>([
  ...Object.values(CURRENT_ANTHROPIC_MODELS),
  ...HISTORICAL_ANTHROPIC_MODELS,
  ...TEST_FIXTURE_MODEL_IDS,
])

/**
 * Type guard for "is this string one of the models the application
 * is allowed to CALL today?" Distinct from KNOWN_ANTHROPIC_MODEL_IDS
 * which also includes historical / test-fixture IDs.
 */
export function isCurrentAnthropicModel(s: string): s is CurrentAnthropicModelId {
  return (Object.values(CURRENT_ANTHROPIC_MODELS) as readonly string[]).includes(s)
}
